/* eslint-disable react/style-prop-object */
import { Placement } from '@popperjs/core';
import { Observer, observer } from 'mobx-react-lite';
import React, { ReactNode, useEffect, useState } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import Box from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import {
  Heading,
  majorScale,
  MenuDivider,
  MenuItem,
  Select,
  Spinner,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import {
  IBOMItem,
  ICompany,
  IProduct,
  ISupplierItem,
  ProductType,
  Unit,
  useStores,
} from '../../stores';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { ProductThumbnail } from './ProductThumbnail';

export interface IProductSelectProps {
  disabled?: boolean;
  value?: ISupplierItem['id'];
  children?: ReactNode;
  onSelect: (item: IProduct) => void;
  onSelectSupplierProductItem: (item: ISupplierItem) => void;
  filter?: IPaginationFilter<ISupplierItem>['filter'];
  placement?: Placement;
  showTitle?: boolean;
}

export const ProductSupplierSelect: React.FC<
  Omit<IProductSelectProps, 'onSelect'>
> = ({
  children,
  onSelectSupplierProductItem,
  disabled = false,
  value,
  filter,
  placement,
  showTitle = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { supplierItemStore: supplierProductItemStore } = useStores();
  const [items, setItems] = useState<ISupplierItem[]>();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedCallback(setQuery, 150);

  useEffect(() => {
    if (!isOpen) return;
    let isSub = true;
    setItems([]);
    const filtering = { filter: { ...filter, search: query } };
    supplierProductItemStore
      .fetchMore(filtering)
      .then((res) => isSub && setItems(res.items));
    return () => {
      isSub = false;
    };
  }, [isOpen, supplierProductItemStore, query, filter]);

  return (
    <Select
      placement={placement}
      disabled={disabled}
      value={value}
      isLoading={!items || supplierProductItemStore.isLoading}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      placeholder="Vælg leverandør"
      items={items || []}
      onSelect={onSelectSupplierProductItem}
      onQueryChange={debouncedQuery.callback}
      resolveSelectedValue={(id) => supplierProductItemStore.getItem(id)}
      itemRenderer={(item) => (
        <Observer>
          {() =>
            item.supplier && item.product ? (
              <Box display="flex" justifyContent="space-between" width="100%">
                <Box>
                  <Box>
                    {showTitle
                      ? item.product.name
                      : (item.supplier as ICompany).name}
                  </Box>
                  <Box className="p small font-mono muted">
                    <FormattedNumber
                      format="currency"
                      currency={item.cost_price_currency}
                      value={item.cost_price}
                    />
                    /<FormattedUnit unit={item.product.unit} />
                  </Box>
                </Box>
                <Box className="muted font-mono">
                  <FormattedUnit value={item.stock} unit={item.product.unit} />
                </Box>
              </Box>
            ) : (
              <Spinner size={16} />
            )
          }
        </Observer>
      )}
      valueRenderer={(item) =>
        item.supplier ? (item.supplier as ICompany).name : ''
      }
    >
      {children}
    </Select>
  );
};

export const ProductSupplierDetailSelect: React.FC<IProductSelectProps> =
  observer(({ children, onSelect, onSelectSupplierProductItem }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { productStore } = useStores();
    const [items, setItems] = useState<IProduct[]>();
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      if (!isOpen) return;
      let isSub = true;
      productStore
        .fetchMore({ filter: { deleted: false, search: query } })
        .then((res) => isSub && setItems(res.items));
      return () => {
        isSub = false;
      };
    }, [isOpen, productStore, query]);

    return (
      <Select
        isLoading={!items || productStore.isLoading}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        title="Vælg produkt"
        items={items || []}
        onSelect={onSelect}
        itemRenderer={(item) => (
          <>
            <ProductThumbnail
              product={item}
              disableLink
              width="100%"
              className="elipsis"
            />
            <Box
              className="p muted small font-mono"
              whiteSpace="nowrap"
              marginLeft="auto"
              textAlign="right"
              paddingLeft={majorScale(1)}
              flexShrink={1}
            >
              {item.sku ? item.sku : `(${item.id})`}
            </Box>
          </>
        )}
        onQueryChange={(q) => debouncedQuery.callback(q)}
        detailViewRenderer={(product) => (
          <DetailViewRenderer
            product={product}
            onSelectProduct={(product) => {
              onSelect(product);
              setIsOpen(false);
            }}
            onSelectSupplierProductItem={(item) => {
              onSelectSupplierProductItem(item);
              setIsOpen(false);
            }}
          />
        )}
        fill={false}
      >
        {children}
      </Select>
    );
  });

const DetailViewRenderer: React.FC<{
  product?: IProduct;
  onSelectProduct: (product: IProduct) => void;
  onSelectSupplierProductItem: (item: ISupplierItem) => void;
}> = observer(({ product, onSelectSupplierProductItem, onSelectProduct }) => {
  const intl = useIntl();

  return product ? (
    <>
      <Heading size={300} padding={majorScale(1)}>
        {product.name}
      </Heading>

      <MenuDivider text="Udsaglspris" />
      <MenuItem
        className="font-mono"
        onClick={() => onSelectProduct(product)}
        text={intl.formatNumber(product.retail_price, {
          style: 'currency',
          currency: product.retail_price_currency || 'DKK',
          currencyDisplay: 'code',
        })}
      />

      {product.type === ProductType.GOOD && <MenuDivider text="Leverandører" />}
      {product.supplier_items.map((item, i) =>
        item ? (
          <Box
            className="item bordered"
            key={item.id}
            onClick={() => onSelectSupplierProductItem(item)}
          >
            <div className="p">
              {item.supplier ? (
                <Box>
                  <Box>{(item.supplier as ICompany).name}</Box>
                  <Box is="p" className="small muted">
                    <FormattedUnit
                      value={item.stock}
                      unit={item.product ? item.product.unit : Unit.PCS}
                    />{' '}
                    på lager
                  </Box>
                </Box>
              ) : (
                <Spinner size={16} />
              )}
            </div>
            <Box
              className="p font-mono small"
              marginLeft="auto"
              paddingLeft={majorScale(4)}
            >
              <FormattedNumber
                style="currency"
                currencyDisplay="code"
                currency={
                  item.cost_price_currency ||
                  product.retail_price_currency ||
                  'DKK'
                }
                value={item.cost_price}
              />
            </Box>
          </Box>
        ) : (
          <Box className="item bordered" key={i}>
            <Spinner size={16} />
          </Box>
        )
      )}
      {product.type === ProductType.PACKAGE && (
        <MenuDivider text="Pakkeløsning" />
      )}
      {product.bill_of_materials.map((item: IBOMItem | undefined, i) =>
        item && item.product ? (
          <Box
            className="item bordered"
            key={i}
            onClick={() => onSelectProduct(item.product)}
          >
            <div className="p">
              {item.product ? (
                (item.product as IProduct).name
              ) : (
                <Spinner size={16} />
              )}
            </div>
            <Box
              className="p font-mono"
              marginLeft="auto"
              paddingLeft={majorScale(4)}
            >
              <FormattedNumber value={item.quantity} />{' '}
              <Box is="span" display="inline-block" minWidth="26px">
                <FormattedUnit unit={(item.product as IProduct).unit} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box className="item bordered" key={i}>
            <Spinner size={16} />
          </Box>
        )
      )}
    </>
  ) : (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <p className="medium">Vælg et product</p>
    </Box>
  );
});
